// Fase 17.3, Plano 11 (D-10): regras puras da tela "Tintas e rendimento" (validação e formatação do rendimento em m²/kg).

import type { TipoTintaRendimento, TintaPadraoDefinidaDto, TintaRendimentoDto, UnidadeTinta } from '@pieltec/contracts';

export const TEXTO_CORRECAO_CADASTRO = 'O atalho abre Cadastros → Materiais com a busca pelo código. Selecione apenas a tinta indicada e use Editar Selecionados; marque Aplicar nos campos faltantes, preencha e confira antes de confirmar.';

export function camposPendentesTinta(tinta: { readonly tipoTinta: string | null; readonly corCodigo: string | null }): string[] {
  return [!tinta.tipoTinta?.trim() ? 'Tipo de tinta' : null, !tinta.corCodigo?.trim() ? 'Cor (código)' : null].filter((campo): campo is string => campo !== null);
}
export const TEXTO_SO_USADAS_SEM_RENDIMENTO = 'Só usadas sem rendimento';
export const MENSAGEM_RENDIMENTO_INVALIDO = 'Informe um rendimento maior que zero, com até 3 casas decimais (ex.: 12,5).';

// Mesmo limite da API (NUMERIC(8,3) e CHECK > 0): até 5 inteiros e 3 decimais, vírgula ou ponto, sem zero.
const REGEX_RENDIMENTO = /^(?!0+(?:[.,]0+)?$)\d{1,5}(?:[.,]\d{1,3})?$/;

/** Campo vazio limpa o rendimento (válido); qualquer outro texto precisa ser um número positivo. */
export function validarRendimento(texto: string): string | null {
  const limpo = texto.trim();
  if (limpo === '') return null;
  return REGEX_RENDIMENTO.test(limpo) ? null : MENSAGEM_RENDIMENTO_INVALIDO;
}

/** Texto do campo -> valor da API (ponto decimal); vazio vira null. */
export function rendimentoParaApi(texto: string): string | null {
  const limpo = texto.trim();
  return limpo === '' ? null : limpo.replace(',', '.');
}

/** Valor da API ('12.500') -> texto do campo em pt-BR ('12,500'); sem valor, campo vazio. */
export function rendimentoParaCampo(valor: string | null): string {
  return valor === null ? '' : valor.replace('.', ',');
}

/** Compara o que está no campo com o valor salvo (ignora diferença de vírgula/ponto e zeros à direita). */
export function rendimentoMudou(valorSalvo: string | null, texto: string): boolean {
  const novo = rendimentoParaApi(texto);
  if (novo === null || valorSalvo === null) return novo !== valorSalvo;
  return Number(novo) !== Number(valorSalvo);
}

export function usadaSemRendimento(tinta: Pick<TintaRendimentoDto, 'pecasLigadas' | 'rendimentoM2PorKg'>): boolean {
  return tinta.pecasLigadas > 0 && tinta.rendimentoM2PorKg === null;
}

export function filtrarUsadasSemRendimento(tintas: readonly TintaRendimentoDto[], somenteUsadasSemRendimento: boolean): readonly TintaRendimentoDto[] {
  if (!somenteUsadasSemRendimento) return tintas;
  return [...tintas].filter(usadaSemRendimento).sort((a, b) => b.pecasLigadas - a.pecasLigadas || a.codigo.localeCompare(b.codigo));
}

export function contarUsadasSemRendimento(tintas: readonly TintaRendimentoDto[]): number {
  return tintas.filter(usadaSemRendimento).length;
}

export function rotuloUnidadeRendimento(unidade: UnidadeTinta): string {
  return unidade === 'l' ? 'm²/L' : 'm²/kg';
}

export function unidadeSugeridaDoTipo(tipo: TipoTintaRendimento): UnidadeTinta {
  return tipo === 'LIQUIDA' ? 'l' : 'kg';
}

export function validarPadraoDoTipo(texto: string, unidade: UnidadeTinta | null): string | null {
  const erro = validarRendimento(texto);
  if (erro !== null) return erro;
  return texto.trim() === '' || unidade !== null ? null : 'Escolha a unidade do rendimento padrão (kg ou litro).';
}

// Quick 261017-gwz: tinta padrão por cor. O grupo inclui unidade, igual ao índice do banco.
export const TEXTO_SO_VARIAS_TINTAS = 'Só cores com várias tintas';

type TintaDoGrupo = Pick<TintaRendimentoDto, 'corCodigo' | 'tipoTinta' | 'unidade'>;

export function chaveGrupoTinta(tinta: TintaDoGrupo): string {
  return `${(tinta.corCodigo ?? '').trim().toUpperCase()}|${tinta.tipoTinta}|${tinta.unidade}`;
}

/** A ação só existe para quem gerencia roteiros, em grupo com mais de uma tinta e para a tinta que ainda não é a padrão. */
export function podeUsarComoPadrao(tinta: Pick<TintaRendimentoDto, 'corCodigo' | 'padrao' | 'tintasNoGrupo'>, podeEditar: boolean): boolean {
  return podeEditar && tinta.corCodigo !== null && tinta.corCodigo.trim() !== '' && tinta.tintasNoGrupo > 1 && !tinta.padrao;
}

export function rotuloUsarComoPadrao(tinta: Pick<TintaRendimentoDto, 'corCodigo'>): string {
  return tinta.corCodigo ? `Usar como padrão para ${tinta.corCodigo.trim()}` : 'Tinta sem cor não pode ser padrão';
}

/** Desligado devolve a lista como veio; ligado fica só com cores de várias tintas, agrupadas (padrão primeiro, depois por código). */
export function filtrarCoresComVariasTintas(tintas: readonly TintaRendimentoDto[], somenteVarias: boolean): readonly TintaRendimentoDto[] {
  if (!somenteVarias) return tintas;
  const grupos = new Map<string, TintaRendimentoDto[]>();
  for (const tinta of tintas) {
    if (tinta.tintasNoGrupo <= 1) continue;
    const chave = chaveGrupoTinta(tinta);
    const grupo = grupos.get(chave);
    if (grupo) grupo.push(tinta);
    else grupos.set(chave, [tinta]);
  }
  return [...grupos.values()].flatMap((grupo) =>
    [...grupo].sort((a, b) => Number(b.padrao) - Number(a.padrao) || (a.codigo < b.codigo ? -1 : a.codigo > b.codigo ? 1 : 0)),
  );
}

/** Marca a escolhida e desmarca as outras do mesmo grupo (espelha a troca feita no servidor); não muta a entrada. */
export function marcarPadraoNoGrupo(tintas: readonly TintaRendimentoDto[], materialId: string): TintaRendimentoDto[] {
  const escolhida = tintas.find((tinta) => tinta.materialId === materialId);
  if (!escolhida) return [...tintas];
  const chave = chaveGrupoTinta(escolhida);
  return tintas.map((tinta) => (chaveGrupoTinta(tinta) === chave ? { ...tinta, padrao: tinta.materialId === materialId } : tinta));
}

export function mensagemPadraoDefinido(tinta: Pick<TintaRendimentoDto, 'codigo' | 'corCodigo'>, anterior: TintaPadraoDefinidaDto['padraoAnterior']): string {
  const base = tinta.corCodigo
    ? `${tinta.codigo} agora é a tinta padrão da cor ${tinta.corCodigo.trim()}`
    : `${tinta.codigo} não pode ser definida como tinta padrão sem cor`;
  return anterior ? `${base}; ${anterior.codigo} deixou de ser.` : `${base}.`;
}
